import type { Api } from "../data/api";
import { ApiError } from "../data/api";
import { Sha256 } from "./sha256";
import { t } from "./sprache";

/**
 * Content-Ablage mit Video (0155): Upload in Teilen mit Fortschritt, Pause und Fortsetzen (auch nach einem Abbruch der
 * Verbindung am Handy), Vorschaubild und Dauer aus dem Browser, Prüfsumme beim Lesen, Stand je Fassung und Zeitmarken.
 * Kein Umwandeln: die Datei geht so in den Speicher, wie sie aufgenommen wurde.
 */

export const MEDIEN_ENDUNGEN = ["mp4", "m4v", "mov", "webm", "jpg", "jpeg", "png", "heic", "webp", "pdf"] as const;
/** accept-Attribut der Dateiauswahl (Kamerarolle am Handy: video/* und image/*) */
export const MEDIEN_ACCEPT = "video/mp4,video/quicktime,video/webm,image/jpeg,image/png,image/heic,image/webp,application/pdf,.mp4,.m4v,.mov,.webm,.jpg,.jpeg,.png,.heic,.webp,.pdf";
export const MEDIEN_MAX_BYTES = 2 * 1024 * 1024 * 1024;

export type MedienArt = "video" | "image" | "pdf";
export function medienArt(name: string): MedienArt | null {
  const e = name.toLowerCase().split(".").pop() ?? "";
  if (["mp4", "m4v", "mov", "webm"].includes(e)) return "video";
  if (["jpg", "jpeg", "png", "heic", "webp"].includes(e)) return "image";
  if (e === "pdf") return "pdf";
  return null;
}

/** Zeitmarke wie „0:12“ bzw. „1:02:03“ */
export function zeitmarke(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const ss = String(s % 60).padStart(2, "0");
  return h ? `${h}:${String(m).padStart(2, "0")}:${ss}` : `${m}:${ss}`;
}

/** „0:12 – Logo größer“ → 12000 ms, sonst null (Eingabe einer Zeitmarke von Hand) */
export function zeitAusText(text: string): number | null {
  const m = /^\s*(?:(\d+):)?(\d{1,2}):(\d{2})\b/.exec(text);
  if (!m) return null;
  return ((Number(m[1] ?? 0) * 60 + Number(m[2])) * 60 + Number(m[3])) * 1000;
}

export function groesse(bytes: number, lang: "de" | "en" = "de"): string {
  const nf = (n: number, d = 1) => n.toLocaleString(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: d });
  if (bytes >= 1024 ** 3) return `${nf(bytes / 1024 ** 3)} GB`;
  if (bytes >= 1024 ** 2) return `${nf(bytes / 1024 ** 2, bytes >= 100 * 1024 ** 2 ? 0 : 1)} MB`;
  return `${nf(Math.max(1, Math.round(bytes / 1024)), 0)} KB`;
}

// ── Stand je Fassung ─────────────────────────────────────────────────────────────────────────────────────────────────────

export type ContentStand = "eingereicht" | "korrektur" | "freigegeben" | "bei_marke" | "marke_frei";
export interface StandFassung {
  status: "submitted" | "changes_requested" | "approved";
  brand?: { status: string; expires_at?: string | null } | null;
}

/** Chip der Ordner-Ansicht: Eingereicht, Korrektur, Freigegeben, Bei der Marke, Von der Marke freigegeben */
export function contentStand(s: StandFassung, jetzt = new Date()): ContentStand {
  const b = s.brand;
  if (b?.status === "approved") return "marke_frei";
  if (b?.status === "open" && (!b.expires_at || new Date(b.expires_at) > jetzt) && s.status !== "changes_requested") return "bei_marke";
  if (s.status === "changes_requested") return "korrektur";
  if (s.status === "approved") return "freigegeben";
  return "eingereicht";
}
export const STAND_TEXT: Record<ContentStand, { de: string; en: string; tone: "info" | "warning" | "success" | "neutral" }> = {
  eingereicht: { de: "Eingereicht", en: "Submitted", tone: "info" },
  korrektur: { de: "Korrektur", en: "Changes", tone: "warning" },
  freigegeben: { de: "Freigegeben", en: "Approved", tone: "success" },
  bei_marke: { de: "Bei der Marke", en: "With the brand", tone: "info" },
  marke_frei: { de: "Von der Marke freigegeben", en: "Approved by the brand", tone: "success" },
};

// ── Vorschaubild und Dauer ───────────────────────────────────────────────────────────────────────────────────────────────

export interface MedienInfo { durationMs: number | null; width: number | null; height: number | null; poster: Blob | null }

/** Dauer, Maße und ein Vorschaubild (JPEG, höchstens 720 px breit) aus der Datei; was der Browser nicht lesen kann, bleibt leer. */
export async function medienInfo(file: File, timeoutMs = 8000): Promise<MedienInfo> {
  const leer: MedienInfo = { durationMs: null, width: null, height: null, poster: null };
  const art = medienArt(file.name);
  if (typeof document === "undefined" || !art || art === "pdf") return leer;
  const url = URL.createObjectURL(file);
  try {
    if (art === "image") {
      const img = new Image();
      img.src = url;
      await Promise.race([img.decode(), new Promise((_, rej) => setTimeout(() => rej(new Error("zeit")), timeoutMs))]);
      // Vorschaubild (höchstens 720 px), damit Ordner und Handy nicht das Original (bis 100 MB) laden
      let poster: Blob | null = null;
      try {
        const w = Math.min(720, img.naturalWidth || 720);
        const h = Math.round(w * ((img.naturalHeight || 1) / (img.naturalWidth || 1)));
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        c.getContext("2d")?.drawImage(img, 0, 0, w, h);
        poster = await new Promise<Blob | null>((res) => c.toBlob(res, "image/jpeg", 0.8));
        if (poster && poster.size > 5 * 1024 * 1024) poster = null;
      } catch {
        poster = null;
      }
      return { ...leer, width: img.naturalWidth || null, height: img.naturalHeight || null, poster };
    }
    const v = document.createElement("video");
    v.preload = "metadata";
    v.muted = true;
    v.playsInline = true;
    v.src = url;
    await new Promise<void>((res, rej) => {
      const timer = setTimeout(() => rej(new Error("zeit")), timeoutMs);
      v.onloadedmetadata = () => { clearTimeout(timer); res(); };
      v.onerror = () => { clearTimeout(timer); rej(new Error("lesen")); };
    });
    const durationMs = Number.isFinite(v.duration) ? Math.round(v.duration * 1000) : null;
    const info: MedienInfo = { durationMs, width: v.videoWidth || null, height: v.videoHeight || null, poster: null };
    try {
      v.currentTime = Math.min(1, (v.duration || 2) / 3);
      await new Promise<void>((res, rej) => {
        const timer = setTimeout(() => rej(new Error("zeit")), timeoutMs);
        v.onseeked = () => { clearTimeout(timer); res(); };
      });
      const w = Math.min(720, v.videoWidth || 720);
      const h = Math.round(w * ((v.videoHeight || 1280) / (v.videoWidth || 720)));
      const c = document.createElement("canvas");
      c.width = w;
      c.height = h;
      c.getContext("2d")?.drawImage(v, 0, 0, w, h);
      info.poster = await new Promise<Blob | null>((res) => c.toBlob(res, "image/jpeg", 0.8));
      if (info.poster && info.poster.size > 5 * 1024 * 1024) info.poster = null;
    } catch {
      /* ohne Vorschaubild */
    }
    return info;
  } catch {
    return leer;
  } finally {
    URL.revokeObjectURL(url);
  }
}

// ── Upload in Teilen ─────────────────────────────────────────────────────────────────────────────────────────────────────

export type UploadStatus = "vorbereiten" | "laeuft" | "pausiert" | "fertig" | "fehler" | "abgebrochen";
export interface UploadZustand {
  key: string;
  name: string;
  size: number;
  loaded: number;
  status: UploadStatus;
  mediaId: string | null;
  error: ApiError | null;
}
export interface UploadZiel { deal: string; deliverable?: string | null; period?: string | null }

const SPEICHER_PREFIX = "co-upload:";
const PARALLEL = 3;

/**
 * Schlüssel zum Fortsetzen: dieselbe Person, dieselbe Datei (Name und Größe) zum selben Ziel. Ohne Änderungszeit: die
 * Kamerarolle am iPhone liefert bei jeder Auswahl eine neue Kopie mit neuer Zeit.
 */
export function uploadSchluessel(file: Pick<File, "name" | "size" | "lastModified">, ziel: UploadZiel, userId: string | null = null): string {
  return `${SPEICHER_PREFIX}${userId ?? "-"}:${ziel.deal}:${ziel.deliverable ?? ziel.period ?? ""}:${file.name}:${file.size}`;
}

/** Abbruch als Fehler (für Pause und Abbrechen nach jedem Warten) */
function abbruch(signal: AbortSignal) {
  if (signal.aborted) throw new DOMException("abgebrochen", "AbortError");
}
/** Warten, das eine Pause sofort beendet */
function warte(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((res, rej) => {
    if (signal.aborted) return rej(new DOMException("abgebrochen", "AbortError"));
    const timer = setTimeout(res, ms);
    signal.addEventListener("abort", () => { clearTimeout(timer); rej(new DOMException("abgebrochen", "AbortError")); }, { once: true });
  });
}
function merken(key: string, wert: { mediaId: string } | null) {
  try {
    if (wert) localStorage.setItem(key, JSON.stringify(wert));
    else localStorage.removeItem(key);
  } catch {
    /* privates Fenster: Fortsetzen nur bis zum Neuladen */
  }
}
function gemerkt(key: string): { mediaId: string } | null {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as { mediaId: string }) : null;
  } catch {
    return null;
  }
}

/**
 * Ein Upload: start() beginnt (oder setzt einen gemerkten fort), pause() hält an (die fertigen Teile bleiben im Speicher),
 * fortsetzen() fragt die fertigen Teile ab und lädt nur die fehlenden, abbrechen() verwirft alles. Fehler einer Verbindung
 * werden je Teil dreimal wiederholt; danach steht der Upload auf „fehler“ und lässt sich fortsetzen.
 */
export class MedienUpload {
  readonly zustand: UploadZustand;
  private ctrl: AbortController | null = null;
  private info: MedienInfo | null = null;

  constructor(private readonly api: Api, private readonly file: File, private readonly ziel: UploadZiel,
              private readonly onChange: (z: UploadZustand) => void) {
    const key = uploadSchluessel(file, ziel, api.session()?.userId ?? null);
    this.zustand = { key, name: file.name, size: file.size, loaded: 0, status: "vorbereiten", mediaId: gemerkt(key)?.mediaId ?? null, error: null };
  }

  private set(p: Partial<UploadZustand>) {
    Object.assign(this.zustand, p);
    this.onChange({ ...this.zustand });
  }

  async start(): Promise<void> {
    if (this.zustand.status === "laeuft" || this.zustand.status === "fertig") return;
    const art = medienArt(this.file.name);
    if (!art) return this.fail(new ApiError(t("Dieser Dateityp geht nicht. Erlaubt sind MP4, MOV, WebM, JPG, PNG, HEIC, WebP und PDF.",
      "This file type is not supported. Allowed: MP4, MOV, WebM, JPG, PNG, HEIC, WebP and PDF."), { hint: "media:type" }));
    if (this.file.size > MEDIEN_MAX_BYTES) return this.fail(new ApiError(t("Eine Datei darf höchstens 2 GB groß sein.", "A file may be at most 2 GB."), { hint: "media:size" }));
    this.ctrl = new AbortController();
    const signal = this.ctrl.signal;
    this.set({ status: "laeuft", error: null });
    try {
      this.info ??= await medienInfo(this.file);
      abbruch(signal);
      let resume = !!this.zustand.mediaId;
      if (!this.zustand.mediaId) {
        const s = await this.api.medien<{ media_id: string }>({ action: "start", deal: this.ziel.deal, deliverable: this.ziel.deliverable ?? null,
          period: this.ziel.period ?? null, name: this.file.name, size: this.file.size, duration_ms: this.info.durationMs, width: this.info.width, height: this.info.height });
        // Während des Starts abgebrochen: die angelegte Datei gleich wieder abbrechen (sonst belegt sie Platz und Kontingent)
        if (this.zustand.status === "abgebrochen") {
          await this.api.medien({ action: "abort", media: s.media_id }).catch(() => undefined);
          return;
        }
        this.set({ mediaId: s.media_id });
        merken(this.zustand.key, { mediaId: s.media_id });
        resume = false;
      }
      abbruch(signal);
      const mediaId = this.zustand.mediaId!;
      // Vorschaubild zuerst (klein), damit die Ordner-Ansicht es sofort zeigt
      let poster = false;
      if (this.info.poster && !resume) {
        try {
          const p = await this.api.medien<{ url: string }>({ action: "poster", media: mediaId, size: this.info.poster.size });
          await this.api.mediaPut(p.url, this.info.poster, { signal, contentType: "image/jpeg" });
          poster = true;
        } catch (e) {
          if ((e as Error).name === "AbortError") throw e;
        }
      } else if (resume && this.info.poster) {
        poster = true;
      }
      const first = await this.api.medien<{ urls: Record<string, string>; total: number; part_size: number; done?: { n: number; etag: string; size: number }[] }>(
        { action: "parts", media: mediaId, numbers: [], resume: true });
      const total = first.total;
      const partSize = first.part_size;
      const len = (n: number) => (n < total ? partSize : this.file.size - (total - 1) * partSize);
      const etags = new Map<number, string>();
      for (const d of first.done ?? []) if (d.size === len(d.n)) etags.set(d.n, d.etag);
      const progress = new Map<number, number>();
      for (const n of etags.keys()) progress.set(n, len(n));
      const melden = () => this.set({ loaded: [...progress.values()].reduce((a, b) => a + b, 0) });
      melden();
      const hash = new Sha256();
      const offen: number[] = [];
      for (let n = 1; n <= total; n++) if (!etags.has(n)) offen.push(n);
      const urls = new Map<number, string>();
      const holeUrls = async (ab: number) => {
        const wanted = offen.filter((n) => n >= ab && !urls.has(n)).slice(0, 20);
        if (!wanted.length) return;
        const r = await this.api.medien<{ urls: Record<string, string> }>({ action: "parts", media: mediaId, numbers: wanted });
        for (const [k, v] of Object.entries(r.urls)) urls.set(Number(k), v);
      };
      /** neuer Link für einen Teil (abgelaufen oder abgelehnt) */
      const neuerLink = async (n: number) => {
        const r = await this.api.medien<{ urls: Record<string, string> }>({ action: "parts", media: mediaId, numbers: [n] });
        const u = r.urls[n];
        if (!u) throw new ApiError(t(`Teil ${n} ließ sich nicht hochladen.`, `Part ${n} could not be uploaded.`), { hint: "media:part" });
        urls.set(n, u);
        return u;
      };
      const laufend = new Set<Promise<void>>();
      // Der erste endgültige Fehler eines Teils bricht den Upload ab (mit seiner Meldung, nicht „Es fehlen Teile“)
      let ersterFehler: unknown = null;
      for (let n = 1; n <= total; n++) {
        abbruch(signal);
        if (ersterFehler) throw ersterFehler;
        const teil = this.file.slice((n - 1) * partSize, (n - 1) * partSize + len(n));
        // Prüfsumme in der Reihenfolge der Datei, auch für schon hochgeladene Teile
        hash.update(new Uint8Array(await teil.arrayBuffer()));
        abbruch(signal);
        if (etags.has(n)) continue;
        if (!urls.has(n)) await holeUrls(n);
        abbruch(signal);
        const job = this.teil(n, teil, () => urls.get(n)!, () => neuerLink(n), signal, (l) => { progress.set(n, l); melden(); })
          .then((etag) => { etags.set(n, etag); }, (e: unknown) => { if (!ersterFehler) ersterFehler = e; });
        laufend.add(job);
        void job.then(() => laufend.delete(job));
        if (laufend.size >= PARALLEL) await Promise.race(laufend);
      }
      await Promise.all(laufend);
      if (ersterFehler) throw ersterFehler;
      abbruch(signal);
      await this.api.medien({ action: "finish", media: mediaId, parts: [...etags.entries()].map(([n, etag]) => ({ n, etag })), sha256: hash.hex(), poster });
      merken(this.zustand.key, null);
      this.set({ status: "fertig", loaded: this.file.size });
    } catch (e) {
      if ((e as Error).name === "AbortError") {
        if (this.zustand.status !== "abgebrochen") this.set({ status: "pausiert" });
        return;
      }
      // Übrige Teile anhalten; die fertigen bleiben im Speicher und zählen beim Fortsetzen
      this.ctrl?.abort();
      const err = e instanceof ApiError ? e : new ApiError((e as Error).message);
      // Abgelaufener oder fremder Upload: neu beginnen statt fortsetzen
      if (["media:not_uploading", "media:size", "media:type_mismatch"].includes(err.hint ?? "") || err.code === "42501") { merken(this.zustand.key, null); this.zustand.mediaId = null; }
      this.fail(err);
    } finally {
      this.ctrl = null;
    }
  }

  private async teil(n: number, data: Blob, url: () => string, neu: () => Promise<string>, signal: AbortSignal, onProgress: (loaded: number) => void): Promise<string> {
    let last: unknown;
    for (let versuch = 0; versuch < 3; versuch++) {
      try {
        return await this.api.mediaPut(url(), data, { signal, onProgress });
      } catch (e) {
        if ((e as Error).name === "AbortError") throw e;
        last = e;
        onProgress(0);
        await warte(1000 * 2 ** versuch, signal);
        // Abgelehnt (Link abgelaufen oder Signatur ungültig): neuer Link statt derselbe noch einmal
        if (e instanceof ApiError && (e.code === "403" || /\(40[03]\)/.test(e.message))) await neu();
      }
    }
    throw last instanceof Error ? last : new ApiError(t(`Teil ${n} ließ sich nicht hochladen.`, `Part ${n} could not be uploaded.`));
  }

  private fail(error: ApiError) {
    this.set({ status: "fehler", error });
  }

  pause() {
    this.ctrl?.abort();
  }

  fortsetzen(): Promise<void> {
    return this.start();
  }

  async abbrechen(): Promise<void> {
    this.set({ status: "abgebrochen" });
    this.ctrl?.abort();
    const id = this.zustand.mediaId;
    merken(this.zustand.key, null);
    if (id) await this.api.medien({ action: "abort", media: id }).catch(() => undefined);
  }
}

/** Datei über einen signierten Link speichern (Download-Link der Serverfunktion mit Content-Disposition) */
export function speichernUnter(url: string, name: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}
